import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { Link } from 'react-router-dom';
import { Paginator } from '@/shared/ui/Paginator/Paginator';
import { ApiOperatorTaskSource, type OperatorTaskListItemResponse } from '@/core/generated/api';
import { useOperatorTasks } from '@/entities/OperatorTask/model/store';
import { createRelatedOperatorTaskStore } from '@/entities/OperatorTask/model/relatedStore';
import type { OperatorTaskOrigin } from '@/entities/OperatorTask/model/types';
import { getTaskOriginKey } from '@/entities/OperatorTask/mappers';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Section } from '@/shared/ui/Section/Section';
import { SidePanel } from '@/shared/ui/SidePanel/SidePanel';
import {
    RequestEmpty,
    RequestError,
    RequestErrorBanner,
    RequestLoading,
} from '@/shared/ui/RequestState';
import { useTaskCompletion } from '../model/useTaskCompletion';
import type { OperatorTaskPanelSelection } from '../model/types';
import { OperatorTaskPanel } from './OperatorTaskPanel';
import { OperatorTaskCard } from './OperatorTaskCard';
import classes from './RelatedOperatorTasks.module.scss';

interface RelatedOperatorTasksProps {
    origin: OperatorTaskOrigin | null;
    variant?: 'order' | 'chat';
    sourceAvailable?: boolean;
    showCreateAction?: boolean;
    selection?: OperatorTaskPanelSelection | null;
    onSelectionChange?: (selection: OperatorTaskPanelSelection | null) => void;
}

/** Показывает общие задачи открытого заказа или диалога */
export const RelatedOperatorTasks = (
    { origin, variant = 'order', sourceAvailable = true, showCreateAction = true, selection: controlledSelection, onSelectionChange }: RelatedOperatorTasksProps
) => {
    const canView = useCanAccessPermission('admin.operator-tasks.view');
    const [store] = useState(() => createRelatedOperatorTaskStore(origin));
    const storedOrigin = useStore(store, (state) => state.origin);
    const loaded = useStore(store, (state) => state.data);
    const status = useStore(store, (state) => state.status);
    const error = useStore(store, (state) => state.error);
    const first = useStore(store, (state) => state.first);
    const requestedPageSize = useStore(store, state => state.pageSize);
    const maxPageSize = useOperatorTasks(state => state.options?.listMaxTake ?? 200);
    const pageSize = Math.min(requestedPageSize, maxPageSize);
    const refreshRevision = useOperatorTasks((state) => state.summarySuccessRevision);
    const key = getTaskOriginKey(origin);
    const sameOrigin = key === getTaskOriginKey(storedOrigin);

    const data = sameOrigin
        ? loaded
        : null;

    const completion = useTaskCompletion(key);
    const [listOpen, setListOpen] = useState(false);
    const [localSelection, setLocalSelection] = useState<OperatorTaskPanelSelection | null>(null);

    const selection = controlledSelection === undefined
        ? localSelection
        : controlledSelection;

    const setSelection = onSelectionChange ?? setLocalSelection;

    useEffect(() => {
        store.getState()
            .setOrigin(canView
                ? origin
                : null);
    }, [canView, origin, store]);

    useEffect(() => {
        if (!canView || !key) return;

        const controller = new AbortController();
        void store.getState().fetch(controller.signal);

        return () => controller.abort();
    }, [canView, key, first, refreshRevision, store, pageSize]);

    /** Открывает задачу выбранного заказа или диалога */
    const openTask = (id: string, editing = false) => {
        setListOpen(false);

        setSelection({
            taskId: id,
            editing
        });
    };

    /** Создаёт задачу с неизменяемой исходной связью */
    const create = () => {
        if (!origin || !key || !sourceAvailable) return;

        setListOpen(false);

        setSelection({
            creating: true,
            origin
        });
    };

    /** Обновляет связанные задачи */
    const reload = () => { void store.getState().fetch(); };

    /** Отмечает выполнение задачи открытого контекста */
    const complete = async (task: OperatorTaskListItemResponse) => {
        await completion.changeStatus(task);
        reload();
    };

    if (!canView) return null;

    const query = new URLSearchParams({ view: 'all' });

    if (origin?.source === ApiOperatorTaskSource.Order && origin.related.id) query.set('order_id', origin.related.id);
    if (origin?.source === ApiOperatorTaskSource.Chat && origin.related.chatId) query.set('chat_id', origin.related.chatId);

    const actions = <div className={classes.actions}>
        {showCreateAction &&
            <ActionButton
                label="Создать задачу"
                icon="pi pi-plus"
                permission="admin.operator-tasks.create"
                outlined={variant === 'chat'}
                disabled={!origin || !key || !sourceAvailable}
                onClick={create}
            />
        }
        {origin &&
            <Link to={`/operator-tasks?${query}`}>Общая очередь</Link>
        }
    </div>;

    const content = <div className={classes.list}>
        <p>Общие задачи для всей смены</p>
        <RequestErrorBanner
            status={status}
            error={error}
            fallbackMessage="Не удалось загрузить задачи"
            onRetry={reload}
        />
        {completion.error &&
            <RequestError
                message={completion.error}
                compact
            />
        }
        {status === RequestStatus.Loading && !data &&
            <RequestLoading message="Загрузка задач..." />
        }
        {status === RequestStatus.Success && data?.allCount === 0 &&
            <RequestEmpty message="Связанных задач пока нет" />
        }
        {data?.items?.map((task) => (
            <OperatorTaskCard
                key={task.id}
                task={task}
                busy={Boolean(task.id && completion.pending.includes(task.id))}
                disabled={status === RequestStatus.Error}
                onOpen={() => { if (task.id) openTask(task.id); }}
                onComplete={() => void complete(task)}
            />
        ))}
        {data &&
            <Paginator
                first={first}
                rows={pageSize}
                totalRecords={data?.allCount ?? 0}
                rowsPerPageOptions={[5, 10, 20, 50, 100].filter(size => size <= maxPageSize)}
                disabled={status !== RequestStatus.Success}
                label="Страницы связанных задач"
                onPageChange={event => {
                    if (event.rows !== pageSize) store.getState().setPageSize(event.rows);
                    else store.getState().setFirst(event.first);
                }}
            />
        }
    </div>;

    return <>
        {origin && variant === 'order' &&
            <Section
                title="Задачи по заказу"
                actions={actions}
            >
                {content}
            </Section>
        }
        {origin && variant === 'chat' &&
            <div className={classes.actions}>
                <ActionButton
                    label={`Задачи (${data?.filterCounts?.inProgress ?? '…'})`}
                    aria-label={`Задачи чата (${data?.filterCounts?.inProgress ?? '…'})`}
                    icon="pi pi-list"
                    outlined
                    size="small"
                    onClick={() => setListOpen(true)}
                />
                <ActionButton
                    icon="pi pi-plus"
                    iconOnly
                    size="small"
                    aria-label="Создать задачу из чата"
                    title="Создать задачу из чата"
                    outlined
                    permission="admin.operator-tasks.create"
                    disabled={!key || !sourceAvailable}
                    onClick={create}
                />
            </div>
        }
        <SidePanel
            visible={variant === 'chat' && listOpen && Boolean(origin)}
            title="Задачи чата"
            onHide={() => setListOpen(false)}
            closable={completion.pending.length === 0}
        >
            {actions}
            {content}
        </SidePanel>
        <OperatorTaskPanel
            taskId={selection?.taskId}
            creating={selection?.creating}
            editing={selection?.editing}
            origin={selection?.origin}
            sourceUnavailable={!sourceAvailable || getTaskOriginKey(selection?.origin) !== key}
            onClose={() => setSelection(null)}
            onOpen={openTask}
            onChanged={reload}
        />
    </>;
};
